/**
 * CampusTrendsTable — 12-week trending table with KPI switching + radio selection.
 */

import { useState, useMemo, useCallback } from 'react';
import usePersistedPerPage from '../../../../hooks/usePersistedPerPage.js';

const KPI_OPTIONS = [
    { key: 'response', label: 'Response Time', field: 'avg_response_time' },
    { key: 'daily', label: 'Daily Events', field: 'avg_daily_events' },
    { key: 'total', label: 'Total Events', field: 'total_events' },
];

const CampusTrendsTable = ({ data, weeks, selectedCampusId, onSelectCampus, kpi, onKpiChange, search, onSearchChange }) => {
    const [sortCol, setSortCol] = useState('campus_name');
    const [sortDir, setSortDir] = useState('asc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = usePersistedPerPage();

    const kpiField = KPI_OPTIONS.find((k) => k.key === kpi)?.field || 'avg_response_time';

    const filtered = useMemo(() => {
        let rows = Array.isArray(data) ? [...data] : [];
        if (search) {
            const q = search.toLowerCase();
            rows = rows.filter((r) => (r.campus_name || '').toLowerCase().includes(q));
        }
        rows.sort((a, b) => {
            const aV = a[sortCol] ?? '';
            const bV = b[sortCol] ?? '';
            if (typeof aV === 'number' && typeof bV === 'number') return sortDir === 'asc' ? aV - bV : bV - aV;
            return sortDir === 'asc' ? String(aV).localeCompare(String(bV), undefined, { numeric: true }) : String(bV).localeCompare(String(aV), undefined, { numeric: true });
        });
        return rows;
    }, [data, search, sortCol, sortDir]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    const safePage = Math.min(page, totalPages);
    const pageRows = filtered.slice((safePage - 1) * perPage, safePage * perPage);

    const handleSort = useCallback((col) => {
        if (sortCol === col) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
        else { setSortCol(col); setSortDir('asc'); }
        setPage(1);
    }, [sortCol]);

    // Build week headers — field format is {kpiField}_{weekNumber} (e.g. avg_response_time_1)
    const weekHeaders = useMemo(() => {
        const headers = [];
        for (let i = 1; i <= 12; i++) {
            const start = weeks?.[`week${i}_start`] || '';
            const end = weeks?.[`week${i}_end`] || '';
            headers.push({ key: `${kpiField}_${i}`, label: start && end ? `${start}–${end}` : `Week ${i}` });
        }
        return headers;
    }, [weeks, kpiField]);

    return (
        <div className="md-section">
            <div className="md-section__header">
                <h3 className="md-section__title">Campus Trends (Last 12 Weeks)</h3>
                <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center' }}>
                    <div className="cd-kpi-selector">
                        {KPI_OPTIONS.map((k) => (
                            <button key={k.key} className={`cd-kpi-btn${kpi === k.key ? ' is-active' : ''}`} onClick={() => onKpiChange(k.key)}>{k.label}</button>
                        ))}
                    </div>
                    <div className="ap-search" style={{ minWidth: 180 }}>
                        <svg className="ap-icon-md ap-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
                        <input type="search" placeholder="Search..." value={search} onChange={(e) => { onSearchChange(e.target.value); setPage(1); }} className="ap-search__input" />
                    </div>
                </div>
            </div>
            <div className="dp-table-wrap" style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
                <table className="dp-table">
                    <thead className="dp-thead">
                        <tr>
                            <th className="dp-th" style={{ width: 40 }}></th>
                            <th className="dp-th" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('campus_name')}>Campus</th>
                            {weekHeaders.map((wh, i) => (
                                <th key={i} className="dp-th" style={{ textAlign: 'center', fontSize: 'var(--font-size-xs)', whiteSpace: 'nowrap' }}>{wh.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.length === 0 ? (
                            <tr className="dp-tr"><td className="dp-td" colSpan={14} style={{ textAlign: 'center' }}><span className="dp-cell-text dp-cell-text--muted">No trend data</span></td></tr>
                        ) : null}
                        {pageRows.map((r) => (
                            <tr key={r.campus_id} className={`dp-tr${selectedCampusId === r.campus_id ? ' is-selected' : ''}`}>
                                <td className="dp-td"><div className="cd-radio-cell"><input type="radio" name="campus-trend-select" checked={selectedCampusId === r.campus_id} onChange={() => onSelectCampus(r)} /></div></td>
                                <td className="dp-td" style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{r.campus_name || '—'}</td>
                                {weekHeaders.map((wh, i) => (
                                    <td key={i} className="dp-td" style={{ textAlign: 'center' }}>{r[wh.key] ?? '-'}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {filtered.length > 0 ? (
                <div className="dp-pagination">
                    <span className="dp-pagination__info">
                        Showing <span className="dp-pagination__emphasis">{(safePage - 1) * perPage + 1}</span>–<span className="dp-pagination__emphasis">{Math.min(safePage * perPage, filtered.length)}</span> of <span className="dp-pagination__emphasis">{filtered.length}</span>
                    </span>
                    <div className="dp-pagination__controls">
                        <div className="dp-per-page">
                            <span className="dp-per-page__label">Per page:</span>
                            <select className="dp-per-page__select" value={perPage} onChange={(e) => { setPerPage(+e.target.value); setPage(1); }}>
                                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                            </select>
                        </div>
                        <div className="dp-page-nav">
                            <button className="dp-page-btn" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>‹</button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).slice(Math.max(0, safePage - 3), Math.min(totalPages, safePage + 2)).map((p) => (
                                <button key={p} className={`dp-page-num${p === safePage ? ' is-active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                            ))}
                            <button className="dp-page-btn" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)}>›</button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default CampusTrendsTable;
